<script>
import Vue from "vue";

const EMPTY_CROP = () => ({
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    rotation: 0,
    resolutionX: 1920,
    resolutionY: 1080,
    mask: null,
    mapX: null,
    mapY: null,
    vlBoxAssignment: [],
});

function mediaName(media) {
    if (!media) return "";
    if (media.name) return media.name;
    if (media.path && typeof media.path === "string" && !media.path.startsWith("data:")) {
        return media.path.split("/").pop();
    }
    return "";
}

function mediaPreviewUrl(media) {
    if (!media) return "";
    if (typeof media.path === "string" && media.path.startsWith("data:")) return media.path;
    if (media.preSignedUrl) return media.preSignedUrl;
    if (media.presignedUrl) return media.presignedUrl;
    return "";
}

export default {
    name: "BuildingCroppingField",
    props: ["propertyName", "value", "object", "options"],

    data() {
        return {
            crops: Array.isArray(this.value) && this.value.length > 0
                ? JSON.parse(JSON.stringify(this.value))
                : [],
            vlboxes: [],
            loadingVlboxes: false,
            vlboxError: null,
        };
    },

    computed: {
        buildingId() {
            return this.object?._id || null;
        },
    },

    watch: {
        buildingId: {
            immediate: true,
            handler() {
                this.loadVlboxes();
            },
        },
        value(newVal) {
            if (Array.isArray(newVal)) {
                this.crops = JSON.parse(JSON.stringify(newVal));
            }
        },
    },

    methods: {
        async loadVlboxes() {
            this.vlboxes = [];
            this.vlboxError = null;
            if (!this.buildingId) return;
            this.loadingVlboxes = true;
            try {
                const devices = await Vue.backoffice.requestor.get("/vlbox", null, {
                    buildingId: this.buildingId,
                });
                this.vlboxes = (Array.isArray(devices) ? devices : [])
                    .filter(d => d.mode === "video" || d.mode === "audio")
                    .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { numeric: true }));
            } catch (err) {
                console.error("BuildingCroppingField: failed to load vlboxes", err);
                this.vlboxError = "Impossible de charger les VLBox.";
            } finally {
                this.loadingVlboxes = false;
            }
        },

        addCrop() {
            this.crops = [...this.crops, EMPTY_CROP()];
            this.emit();
        },

        removeCrop(index) {
            this.crops = this.crops.filter((_, i) => i !== index);
            this.emit();
        },

        updateCropField(index, field, value) {
            const updated = [...this.crops];
            updated[index] = { ...updated[index], [field]: value };
            this.crops = updated;
            this.emit();
        },

        toggleVlbox(index, vlboxId) {
            const crop = this.crops[index];
            const assignment = Array.isArray(crop.vlBoxAssignment) ? [...crop.vlBoxAssignment] : [];
            const id = String(vlboxId);
            const pos = assignment.findIndex(v => String(v) === id);
            if (pos >= 0) {
                assignment.splice(pos, 1);
            } else {
                assignment.push(vlboxId);
            }
            this.updateCropField(index, "vlBoxAssignment", assignment);
        },

        isVlboxAssigned(index, vlboxId) {
            const crop = this.crops[index];
            if (!Array.isArray(crop?.vlBoxAssignment)) return false;
            return crop.vlBoxAssignment.some(v => String(v) === String(vlboxId));
        },

        openFileInput(index, field) {
            const ref = this.$refs[`file-${index}-${field}`];
            const el = Array.isArray(ref) ? ref[0] : ref;
            if (el) el.click();
        },

        readFile(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.addEventListener("load", () => resolve(reader.result));
                reader.addEventListener("error", reject);
                reader.readAsDataURL(file);
            });
        },

        async fileChanged(index, field, event) {
            const file = event.target.files && event.target.files[0];
            if (!file) return;
            const dataUri = await this.readFile(file);
            this.updateCropField(index, field, {
                path: dataUri,
                mimetype: file.type || "application/octet-stream",
                name: file.name,
                preSignedUrl: "",
            });
            event.target.value = "";
        },

        clearFile(index, field) {
            this.updateCropField(index, field, null);
        },

        emit() {
            this.$emit("input", this.crops);
        },

        mediaName,
        mediaPreviewUrl,
    },
};
</script>

<template>
    <div class="BuildingCroppingField">
        <div v-if="vlboxError" class="bc-error">{{ vlboxError }}</div>
        <div v-if="loadingVlboxes" class="bc-info">Chargement des VLBox...</div>

        <div v-for="(crop, index) in crops" :key="index" class="crop-zone">
            <div class="crop-zone-header">
                <strong>Zone {{ index + 1 }}</strong>
                <button type="button" class="Button LittleButton Shy bc-danger" @click="removeCrop(index)">
                    Supprimer
                </button>
            </div>

            <!-- Coordonnées de découpage -->
            <div class="crop-grid">
                <label>
                    <span>Top</span>
                    <input type="number" :value="crop.top" @input="updateCropField(index, 'top', Number($event.target.value))" />
                </label>
                <label>
                    <span>Left</span>
                    <input type="number" :value="crop.left" @input="updateCropField(index, 'left', Number($event.target.value))" />
                </label>
                <label>
                    <span>Bottom</span>
                    <input type="number" :value="crop.bottom" @input="updateCropField(index, 'bottom', Number($event.target.value))" />
                </label>
                <label>
                    <span>Right</span>
                    <input type="number" :value="crop.right" @input="updateCropField(index, 'right', Number($event.target.value))" />
                </label>
                <label>
                    <span>Rotation (°)</span>
                    <input type="number" :value="crop.rotation" step="0.1" @input="updateCropField(index, 'rotation', Number($event.target.value))" />
                </label>
                <label>
                    <span>Résolution X</span>
                    <input type="number" :value="crop.resolutionX" min="1" @input="updateCropField(index, 'resolutionX', Number($event.target.value))" />
                </label>
                <label>
                    <span>Résolution Y</span>
                    <input type="number" :value="crop.resolutionY" min="1" @input="updateCropField(index, 'resolutionY', Number($event.target.value))" />
                </label>
            </div>

            <!-- Fichiers mask / mapX / mapY -->
            <div class="crop-files">
                <div v-for="field in ['mask', 'mapX', 'mapY']" :key="field" class="crop-file-row">
                    <span class="crop-file-label">{{ field }}</span>
                    <button type="button" class="Button LittleButton Shy" @click="openFileInput(index, field)">
                        {{ crop[field] ? 'Remplacer' : 'Choisir' }}
                    </button>
                    <input
                        :ref="`file-${index}-${field}`"
                        type="file"
                        accept="image/*"
                        style="display:none"
                        @change="fileChanged(index, field, $event)"
                    />
                    <span v-if="crop[field]" class="crop-file-name">{{ mediaName(crop[field]) }}</span>
                    <img
                        v-if="crop[field] && mediaPreviewUrl(crop[field])"
                        :src="mediaPreviewUrl(crop[field])"
                        class="crop-file-preview"
                        alt=""
                    />
                    <button v-if="crop[field]" type="button" class="bc-link-danger" @click="clearFile(index, field)">
                        Supprimer
                    </button>
                </div>
            </div>

            <!-- Affectation VLBox -->
            <div class="crop-vlboxes">
                <span class="crop-section-label">VLBox assignées</span>
                <div v-if="vlboxes.length === 0 && !loadingVlboxes" class="bc-info">
                    Aucune VLBox vidéo/audio disponible.
                </div>
                <div class="vlbox-chips">
                    <button
                        v-for="box in vlboxes"
                        :key="box._id"
                        type="button"
                        class="vlbox-chip"
                        :class="{ active: isVlboxAssigned(index, box._id) }"
                        @click="toggleVlbox(index, box._id)"
                    >
                        {{ box.name }}
                    </button>
                </div>
            </div>
        </div>

        <button type="button" class="Button LittleButton Shy bc-add" @click="addCrop">
            + Ajouter une zone de découpage
        </button>
    </div>
</template>

<style lang="scss" scoped>
@import "../../../packages/@snark/backoffice/src/sass/variables";

.BuildingCroppingField {
    display: flex;
    flex-direction: column;
    gap: $gap;

    .bc-error {
        color: $error;
        padding: $gap3;
    }

    .bc-info {
        color: $textColor;
        font-size: 13px;
        padding: $gap3;
    }

    .bc-danger {
        color: $error;
        border-color: $error;
    }

    .bc-link-danger {
        border: none;
        background: transparent;
        color: $error;
        cursor: pointer;
        font-size: 12px;
        padding: 0 4px;
    }

    .bc-add {
        align-self: flex-start;
    }

    .crop-zone {
        border: 1px solid $buttonShyBorder;
        border-radius: 6px;
        padding: $gap;
        display: flex;
        flex-direction: column;
        gap: $gap2;
        background: $mainBackground;
    }

    .crop-zone-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        strong {
            font-size: 14px;
            color: $title2Color;
        }
    }

    .crop-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: $gap2;

        label {
            display: flex;
            flex-direction: column;
            gap: 4px;

            span {
                font-size: 12px;
                color: $textColor;
            }

            input {
                border: 1px solid $buttonShyBorder;
                border-radius: 4px;
                padding: 6px 8px;
                background: $inputBackground;
                color: $title2Color;
                width: 100%;
                font-size: 13px;
            }
        }
    }

    .crop-files {
        display: flex;
        flex-direction: column;
        gap: $gap2;
    }

    .crop-file-row {
        display: flex;
        align-items: center;
        gap: $gap2;
        flex-wrap: wrap;
    }

    .crop-file-label {
        font-size: 13px;
        font-weight: bold;
        color: $textColor;
        min-width: 50px;
    }

    .crop-file-name {
        font-size: 12px;
        color: $textColor;
        max-width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .crop-file-preview {
        display: block;
        max-width: 120px;
        max-height: 80px;
        border-radius: 4px;
        object-fit: contain;
        border: 1px solid $buttonShyBorder;
    }

    .crop-vlboxes {
        display: flex;
        flex-direction: column;
        gap: $gap3;
    }

    .crop-section-label {
        font-size: 12px;
        color: $textColor;
        font-weight: bold;
    }

    .vlbox-chips {
        display: flex;
        flex-wrap: wrap;
        gap: $gap3;
    }

    .vlbox-chip {
        border: 1px solid $buttonShyBorder;
        border-radius: 20px;
        padding: 4px 12px;
        font-size: 12px;
        background: $inputBackground;
        color: $textColor;
        cursor: pointer;

        &.active {
            background: $buttonBackground;
            color: $buttonText;
            border-color: $buttonBackground;
        }
    }
}
</style>
